import moment, { Moment } from 'moment-timezone'
import { Dispatch } from 'redux'
import {
    FC,
    PropsWithChildren,
    Dispatch as ReactDispatch,
    SetStateAction,
} from 'react'
import { useLocation } from 'react-use'
import { FilterFilled } from '@ant-design/icons'
import { Col, Tag } from 'antd'
import styles from '@OpsQuality/styles.css'
import { DrawerCheckBoxSourceType } from '@ProdWb/types'

import getColor from '~/lib/getColor'
import Box from '~/core/Box'
import Text from '~/core/Text'
import Button from '~/core/Button'
import {
    JobRequestListPagePlacement,
    PRIORITY_DISPLAY_VALUES,
} from '../constants'
import { UrlFilterType } from '../types'

import { QualityTopSearch } from './QualityTopSearch'
import { CheckboxAppliedFilter, InspectionTagFilter } from './TableFilters'
import { getUrlFilter, useMPCountries } from './logic'

type FilterActionsType = {
    toggleFilters: () => void
    pendingSearchType?: string
    pendingSearchString: string
    onEnterSearch: (value: string, remainingSearchType: string) => void
    setPendingSearchType: ReactDispatch<SetStateAction<string | undefined>>
    setPendingSearchString: ReactDispatch<SetStateAction<string>>
    setPendingFromDate: ReactDispatch<
        SetStateAction<moment.Moment | null | undefined>
    >
    setPendingToDate: ReactDispatch<
        SetStateAction<moment.Moment | null | undefined>
    >
    setPendingStatusFilters: ReactDispatch<
        SetStateAction<string[] | null | undefined>
    >
    setPendingPriorityFilters: ReactDispatch<
        SetStateAction<string[] | null | undefined>
    >
    setInspectionStatusFilter: ReactDispatch<
        SetStateAction<string[] | null | undefined>
    >
    setPayoutFilter: ReactDispatch<SetStateAction<string[] | null | undefined>>
    getDefaultFromDate: () => moment.Moment
    getDefaultToDate: () => moment.Moment
    createFilterUrl: ({
        remainingPriorityFilters,
        remainingStatusFilters,
        remainingProcessTypeFilters,
        remainingFromDate,
        remainingToDate,
        remainingRange,
        remainingSearchString,
        remainingSearchType,
        remainingMfgDriFilter,
        remainingInspectionStatusFilter,
        remainingPayoutFilter,
        remainingCountryFilters,
        remainingBusinessUnits,
    }: {
        remainingPriorityFilters?: string[] | null
        remainingStatusFilters?: string[] | null
        remainingProcessTypeFilters?: string[] | null
        remainingFromDate?: Moment | null
        remainingToDate?: Moment | null
        remainingRange?: string
        remainingSearchString?: string
        remainingSearchType?: string
        remainingMfgDriFilter?: string[] | null
        remainingInspectionStatusFilter?: string[] | null
        remainingPayoutFilter?: string[] | null
        remainingCountryFilters?: string[] | null
        remainingBusinessUnits?: string[] | null
    }) => ReturnType<Dispatch>
    onCloseTag: (selectedFilters: string[], filterTitle: string) => void
    processTypes?: DrawerCheckBoxSourceType[]
    businessUnits?: DrawerCheckBoxSourceType[]
    salesDriData?: DrawerCheckBoxSourceType[]
    mpCountries: ReturnType<typeof useMPCountries>
}

const FilterActions: FC<PropsWithChildren<FilterActionsType>> = props => {
    const {
        toggleFilters,
        pendingSearchType,
        pendingSearchString,
        onEnterSearch,
        setPendingSearchString,
        setPendingSearchType,
        createFilterUrl,
        setPendingFromDate,
        setPendingToDate,
        getDefaultFromDate,
        getDefaultToDate,
        setPayoutFilter,
        setPendingPriorityFilters,
        setPendingStatusFilters,
        setInspectionStatusFilter,
        onCloseTag,
        processTypes,
        salesDriData,
        mpCountries,
        businessUnits,
    } = props

    const location = useLocation()

    const urlFilters: UrlFilterType = getUrlFilter(location)
    const {
        searchString,
        searchType,
        fromDate,
        toDate,
        range,
        statuses,
        priorityTypes,
        platformProcessTypeIds,
        businessUnits: businessUnitUrlFilter,
        inspectionStatus,
        mfgDriIds,
        payout,
        countryIds,
    } = urlFilters

    return (
        <>
            <Box direction="row" className={styles.filterContainerWrap}>
                <Box
                    direction="row"
                    flex={1}
                    justifyContent="between"
                    className={styles.filterContainer}
                >
                    <QualityTopSearch
                        pendingSearchType={pendingSearchType}
                        pendingSearchString={pendingSearchString}
                        searchType={searchType}
                        onEnterSearch={onEnterSearch}
                        setPendingSearchType={setPendingSearchType}
                        setPendingSearchString={setPendingSearchString}
                    />
                    <div>
                        <Button
                            name="filter-button"
                            placement={JobRequestListPagePlacement}
                            data-test-target="filter-drawer-button"
                            style={{
                                color: getColor('blackAlpha65'),
                            }}
                            onClick={() => toggleFilters()}
                        >
                            <FilterFilled
                                style={{
                                    fontSize: '14px',
                                }}
                            />
                            <Text fontWeight={400}>Filter jobs</Text>
                        </Button>
                    </div>
                </Box>
            </Box>
            <Box direction="row" justifyContent="between">
                <Col xs={0} sm={24}>
                    {!searchString && (fromDate || toDate) ? (
                        <Tag
                            closable
                            onClose={() => {
                                createFilterUrl({
                                    remainingFromDate: null,
                                    remainingToDate: null,
                                })
                                setPendingFromDate(null)
                                setPendingToDate(null)
                            }}
                        >
                            {moment(fromDate).format('MM/DD/YYYY')} -{' '}
                            {moment(toDate).format('MM/DD/YYYY')}
                        </Tag>
                    ) : null}
                    {!searchString &&
                    range === undefined &&
                    fromDate === undefined &&
                    toDate === undefined ? (
                        <Tag
                            closable
                            onClose={() => {
                                createFilterUrl({
                                    remainingFromDate: null,
                                    remainingToDate: null,
                                })

                                setPendingFromDate(null)
                                setPendingToDate(null)
                            }}
                        >
                            {getDefaultFromDate().format('MM/DD/YYYY')} -{' '}
                            {getDefaultToDate().format('MM/DD/YYYY')}
                        </Tag>
                    ) : null}
                    {range ? (
                        <Tag
                            closable
                            onClose={() => {
                                createFilterUrl({})
                            }}
                        >
                            {range}
                        </Tag>
                    ) : null}
                    {statuses
                        ? statuses.map(status => {
                              const remainingStatusFilters = statuses.filter(
                                  currentStatus => currentStatus !== status,
                              )

                              return (
                                  <Tag
                                      closable
                                      key={status}
                                      onClose={() => {
                                          createFilterUrl({
                                              remainingStatusFilters,
                                          })
                                          setPendingStatusFilters(
                                              remainingStatusFilters,
                                          )
                                      }}
                                  >
                                      {`Status: ${status}`}
                                  </Tag>
                              )
                          })
                        : null}
                    {priorityTypes
                        ? priorityTypes.map(priority => {
                              const remainingPriorityFilters =
                                  priorityTypes.filter(
                                      currentPriority =>
                                          currentPriority !== priority,
                                  )

                              return (
                                  <Tag
                                      closable
                                      key={priority}
                                      onClose={() => {
                                          createFilterUrl({
                                              remainingPriorityFilters,
                                          })
                                          setPendingPriorityFilters(
                                              remainingPriorityFilters,
                                          )
                                      }}
                                  >
                                      {`Priority: ${
                                          PRIORITY_DISPLAY_VALUES[
                                              priority as keyof typeof PRIORITY_DISPLAY_VALUES
                                          ]
                                      }`}
                                  </Tag>
                              )
                          })
                        : null}
                    {platformProcessTypeIds
                        ? platformProcessTypeIds.map(typeId => (
                              <CheckboxAppliedFilter
                                  key={typeId}
                                  selectedValues={platformProcessTypeIds}
                                  currentId={typeId}
                                  onCloseTag={onCloseTag}
                                  options={processTypes}
                                  filterTitle="Process type"
                              />
                          ))
                        : null}
                    {businessUnitUrlFilter
                        ? businessUnitUrlFilter.map(businessUnit => (
                              <CheckboxAppliedFilter
                                  key={businessUnit}
                                  selectedValues={businessUnitUrlFilter}
                                  currentId={businessUnit}
                                  onCloseTag={onCloseTag}
                                  options={businessUnits}
                                  filterTitle="Business unit"
                              />
                          ))
                        : null}
                    <InspectionTagFilter
                        inspectionStatus={inspectionStatus}
                        onClose={(
                            remainingInspectionStatusFilter: string[],
                        ) => {
                            createFilterUrl({
                                remainingInspectionStatusFilter,
                            })
                            setInspectionStatusFilter(
                                remainingInspectionStatusFilter,
                            )
                        }}
                    />
                    {payout && (
                        <Tag
                            closable
                            onClose={() => {
                                createFilterUrl({
                                    remainingPayoutFilter: [],
                                })
                                setPayoutFilter(null)
                            }}
                        >
                            {payout?.length === 2
                                ? `Payout: between ${payout[0] || 0} and ${
                                      payout[1] || 0
                                  }`
                                : 'Payout: Unknown'}
                        </Tag>
                    )}
                    {mfgDriIds &&
                        mfgDriIds.map(mfgDriId => (
                            <CheckboxAppliedFilter
                                key={mfgDriId}
                                selectedValues={mfgDriIds}
                                currentId={mfgDriId}
                                onCloseTag={onCloseTag}
                                options={salesDriData}
                                filterTitle="DRI"
                            />
                        ))}
                    {countryIds &&
                        countryIds.map(countryId => (
                            <CheckboxAppliedFilter
                                key={countryId}
                                selectedValues={countryIds}
                                currentId={countryId}
                                onCloseTag={onCloseTag}
                                options={mpCountries}
                                filterTitle="Country"
                            />
                        ))}
                </Col>
            </Box>
        </>
    )
}

export default FilterActions
